import { Button, Box, Tooltip, CircularProgress } from "@mui/material";
import {
  Add as AddIcon,
  Edit as SetIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { Location } from "../../../../models";
import { TimerDisplay } from "../../../../components/core/TimerDisplay";

export interface ActionButtonsSectionProps {
  canClearLocation?: boolean;
  location?: Location | null;
  selectedGroupId?: number | null;
  hasValidEntryData?: boolean;
  disableAddButton?: boolean;
  isLoading?: boolean;
  onAdd?: () => void;
  onSetCount?: () => void;
  onClear?: () => void;
  onClose?: () => void;
  getSeconds?: () => number;
  isRefreshing?: boolean;
  customButtons?: {
    addLabel?: string;
    setCountLabel?: string;
    hideOtherButtons?: boolean;
  };
}

export const ActionButtonsSection = ({
  canClearLocation = false,
  location,
  selectedGroupId,
  hasValidEntryData = false,
  disableAddButton = false,
  isLoading = false,
  onAdd,
  onSetCount,
  onClear,
  onClose,
  getSeconds,
  isRefreshing = false,
  customButtons,
}: ActionButtonsSectionProps) => {
  // Permission checks
  const canAddBillets = true; // Add billets is always allowed when modal is accessible
  const canSetCount = selectedGroupId !== null; // Set count allowed when group is selected
  // canClearLocation is passed as prop from parent

  // Location Direction Type validation - prevent manual inputs for 'O' (Output only) and 'N' (None) locations
  const locationAllowsManualInputs =
    location &&
    (location.LocationDirectionType === "I" ||
      location.LocationDirectionType === "B");

  // Location clearing validation - requires both user role AND location property
  const locationAllowsClearing = location?.ClearEnabled === true;

  // Button states - matching old system behavior
  const isAddDisabled =
    !canAddBillets ||
    !locationAllowsManualInputs ||
    isLoading ||
    disableAddButton;
  const isSetDisabled = !canSetCount || isLoading;
  const isClearDisabled =
    !canClearLocation || !locationAllowsClearing || isLoading;

  // Tooltips
  const getAddTooltip = () => {
    if (!canAddBillets) return "You don't have permission to add billets";
    if (!locationAllowsManualInputs)
      return "This location does not allow manual inputs";
    if (isLoading) return "Operation in progress";
    return "Add new billet group to this location";
  };

  const getSetTooltip = () => {
    if (!selectedGroupId) return "Please select a billet group first";
    if (isLoading) return "Operation in progress";
    return "Update the count of the selected billet group";
  };

  const getClearTooltip = () => {
    if (!canClearLocation) {
      return "You don't have permission to clear this location";
    }
    if (!locationAllowsClearing) {
      return "This location is not configured to allow clearing";
    }
    if (isLoading) return "Operation in progress";
    return "Clear all billets from this location";
  };

  return (
    <Box
      sx={{
        padding: "24px",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        display: "flex",
        gap: (theme) => theme.spacing(2),
        flexWrap: "wrap",
        width: "100vw",
        marginLeft: "calc(-50vw + 50%)",
        boxSizing: "border-box",
      }}
    >
      {/* Add Button */}
      <Tooltip title={getAddTooltip()} arrow>
        <span>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon sx={{ fontSize: "24px" }} />}
            onClick={onAdd}
            disabled={isAddDisabled}
            sx={{
              minWidth: "200px",
              minHeight: "64px",
              fontSize: "20px",
              fontWeight: "bold",
              padding: "16px 48px",
              borderRadius: "8px",
              boxShadow: "0 4px 14px 0 rgba(63, 81, 181, 0.25)",
              transition: "all 0.2s ease",
            }}
          >
            {customButtons?.addLabel ?? "Add"}
            {isLoading && onAdd && (
              <CircularProgress
                size={24}
                sx={{
                  color: "inherit",
                  marginLeft: (theme) => theme.spacing(1),
                }}
              />
            )}
          </Button>
        </span>
      </Tooltip>

      {/* Set Count Button */}
      <Tooltip title={getSetTooltip()} arrow>
        <span>
          <Button
            variant="contained"
            color="inherit"
            startIcon={<SetIcon sx={{ fontSize: "24px" }} />}
            onClick={onSetCount}
            disabled={isSetDisabled}
            sx={{
              minWidth: "200px",
              minHeight: "64px",
              fontSize: "20px",
              fontWeight: "bold",
              padding: "16px 48px",
              borderRadius: "8px",
              boxShadow: "0 4px 14px 0 rgba(158, 158, 158, 0.25)",
              transition: "all 0.2s ease",
            }}
          >
            {customButtons?.setCountLabel ?? "Set"}
            {isLoading && onSetCount && (
              <CircularProgress
                size={24}
                sx={{
                  color: "inherit",
                  marginLeft: (theme) => theme.spacing(1),
                }}
              />
            )}
          </Button>
        </span>
      </Tooltip>

      {/* Clear Button */}
      {!customButtons?.hideOtherButtons && (
        <Tooltip title={getClearTooltip()} arrow>
          <span>
            <Button
              variant="contained"
              startIcon={<ClearIcon sx={{ fontSize: "24px" }} />}
              onClick={onClear}
              disabled={isClearDisabled}
              sx={{
                minWidth: "200px",
                minHeight: "64px",
                fontSize: "20px",
                fontWeight: "bold",
                padding: "16px 48px",
                borderRadius: "8px",
                backgroundColor: (theme) => theme.palette.error.main,
                color: (theme) => theme.palette.error.contrastText,
                boxShadow: "0 4px 14px 0 rgba(244, 67, 54, 0.25)",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.error.dark,
                },
                "&:disabled": {
                  backgroundColor: (theme) => theme.palette.action.disabled,
                  color: (theme) => theme.palette.action.disabled,
                },
              }}
            >
              Clear
              {isLoading && onClear && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "inherit",
                    marginLeft: (theme) => theme.spacing(1),
                  }}
                />
              )}
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Close Button */}
      {!customButtons?.hideOtherButtons && (
        <Tooltip title="Close location content edition" arrow>
          <Button
            variant="contained"
            onClick={onClose}
            disabled={isLoading}
            sx={{
              minWidth: "200px",
              minHeight: "64px",
              fontSize: "20px",
              fontWeight: "bold",
              padding: "16px 48px",
              borderRadius: "8px",
              backgroundColor: (theme) =>
                theme.palette.billetYard.backgrounds.light,
              color: (theme) => theme.palette.text.primary,
              border: (theme) =>
                `1px solid ${theme.palette.billetYard.borders.dark}`,
              boxShadow: "0 4px 14px 0 rgba(158, 158, 158, 0.25)",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.grey[300],
              },
            }}
          >
            Close
          </Button>
        </Tooltip>
      )}

      {/* Timer Display - positioned on the right */}
      {getSeconds && (
        <Box sx={{ position: "absolute", right: "24px" }}>
          <TimerDisplay getSeconds={getSeconds} isRefreshing={isRefreshing} />
        </Box>
      )}
    </Box>
  );
};
